/**
 * Pure helpers for WhatsApp template header media: kind detection from header
 * URLs, file-input `accept` mapping and picked-file validation. Shared by the
 * chat template modal (WabTemplatesVariablesAction) and the bulk message
 * modal (SelectTemplate) — BUG-567 — and by the 1:1 chat template picker
 * (WabTemplatesAction/Template) for header-media prefill — BUG-817.
 *
 * Two sibling concerns live here, and they answer different questions on
 * purpose (BUG-866, KTD1): the `templateHeader*` predicates read the header's
 * declared `format` to decide whether media is required at all, while
 * `resolveTemplateHeaderMediaUrl` stays format-blind because "which URL may I
 * prefill?" depends on the URL's provenance and never on the format.
 */

import { hostnameMatchesDomain } from 'src/functions/url.functions';

/**
 * Media kind expected by a template's header, derived from the header example
 * URL. `file` means the kind could not be determined (e.g. a
 * scontent.whatsapp.net header_handle without a recognizable extension) and
 * any supported template media is accepted.
 */
export type tTemplateMediaKind = 'image' | 'video' | 'document' | 'file';

/**
 * File extensions WhatsApp template headers support per media kind. This is
 * the single source of truth for URL validation, post-pick file validation
 * and the messaging around them — Meta only accepts these for header media,
 * so a HEIC/WebP pick must be rejected with guidance instead of silently
 * disabling the send.
 */
export const TEMPLATE_MEDIA_EXTENSIONS: Record<tTemplateMediaKind, string[]> =
  {
    image: ['.png', '.jpg', '.jpeg'],
    video: ['.mp4'],
    document: ['.pdf'],
    file: ['.png', '.jpg', '.jpeg', '.mp4', '.pdf'],
  };

/**
 * Extracts the template media file type from a header URL. Uses `includes`
 * (not `endsWith`) on purpose: Meta's header_handle URLs carry the extension
 * mid-path followed by query params (`.../1234_n.jpg?ccb=...&oe=...`).
 */
export function templateFileTypeFromUrl(url: string): string {
  if (!url) return '';

  const urlLower = url.toLowerCase();

  if (urlLower.includes('.png')) return 'png';
  if (urlLower.includes('.jpg')) return 'jpg';
  if (urlLower.includes('.jpeg')) return 'jpeg';
  if (urlLower.includes('.mp4')) return 'mp4';
  if (urlLower.includes('.pdf')) return 'pdf';

  return '';
}

/**
 * Maps a header URL to the media kind the template expects.
 */
export function mediaKindFromUrl(url: string): tTemplateMediaKind {
  const fileType = templateFileTypeFromUrl(url);
  if (['png', 'jpg', 'jpeg'].includes(fileType)) return 'image';
  if (fileType === 'mp4') return 'video';
  if (fileType === 'pdf') return 'document';
  return 'file';
}

/**
 * The single `switch` over a template HEADER's declared `format`, from which
 * all three exported format predicates below are derived so they cannot drift
 * apart (BUG-866, KTD2).
 *
 * Takes `format` as a plain string, never `eFormatType`: the value arrives
 * from Meta's payload, so an unrecognized one must fall through benignly
 * rather than be asserted into an enum — and it keeps `src/utils/` free of
 * `src/pages/` imports. The recognized set mirrors `eFormatType`
 * (`src/pages/WhatsappApi/WhatsappApi.enum.ts`): TEXT plus the four media
 * formats, of which `MEDIA` is the legacy generic the backend still returns
 * for templates that predate the split.
 *
 * `requiresMedia` — does this header demand an outbound media component?
 * `kind`          — the concrete kind the format itself fixes, or `undefined`
 *                   when only the example URL can tell (generic `MEDIA`).
 */
function classifyHeaderFormat(format?: string | null): {
  requiresMedia: boolean;
  kind?: 'image' | 'video' | 'document';
} {
  switch (format?.trim().toUpperCase()) {
    case 'IMAGE':
      return { requiresMedia: true, kind: 'image' };
    case 'VIDEO':
      return { requiresMedia: true, kind: 'video' };
    case 'DOCUMENT':
      return { requiresMedia: true, kind: 'document' };
    case 'MEDIA':
      // Legacy generic format: it declares media, but not which kind.
      return { requiresMedia: true };
    default:
      // TEXT, absent, or a format we do not recognize. Never demands media:
      // this predicate can only ever widen what the callers gate on (KTD8),
      // so an unknown value must not be able to block a working send.
      return { requiresMedia: false };
  }
}

/**
 * True when the template's header demands an outbound media component.
 *
 * Answers a different question from `resolveTemplateHeaderMediaUrl` below,
 * which stays format-blind on purpose: that one asks *"which URL may I prefill
 * on the user's behalf?"* — a question about the URL's provenance. This one
 * asks *"does this header require a file at all?"*, which is unanswerable from
 * the URL by construction, because the whole bug is that there is no URL:
 * Meta returns the example of a Business-Manager-created media header as an
 * opaque upload handle (`4:MDFN…:aW1hZ2UvanBlZw==:ARbV…`). Every surface that
 * inferred "needs media" from `startsWith('https://')` therefore read a media
 * template as a plain one and sent it with no media, which Meta rejects with
 * 132012 / `wba-10038` (BUG-866).
 */
export function templateHeaderRequiresMedia(format?: string | null): boolean {
  return classifyHeaderFormat(format).requiresMedia;
}

/**
 * Media kind to render the header uploader for: the `accept` filter, the
 * labels and the inline format error. Prefers the header's declared `format`
 * and falls back to inferring the kind from the example URL, which is all the
 * legacy generic `MEDIA` format and format-less rows can offer. `file` means
 * undeterminable — any supported template media is then accepted (BUG-866).
 */
export function templateHeaderMediaKind(
  format?: string | null,
  url?: string | null,
): tTemplateMediaKind {
  return classifyHeaderFormat(format).kind ?? mediaKindFromUrl(url ?? '');
}

/**
 * True when the attached header media can be previewed in an `<img>`. A PDF or
 * a video in an `<img>` renders as a broken image (BUG-1039). For the generic
 * `file` kind the attached URL decides, since any supported media fits it.
 */
export function isTemplateMediaPreviewable(
  expectedKind: tTemplateMediaKind,
  url: string,
): boolean {
  if (expectedKind !== 'file') return expectedKind === 'image';
  return mediaKindFromUrl(url) === 'image';
}

/**
 * Concrete media type to put on the wire for a header, or `undefined` when
 * none should be sent. Stricter than `templateHeaderMediaKind` in both
 * directions, because the wire needs a definite type and a header that
 * declares no media must not acquire one: a format outside the media set
 * yields `undefined`, and so does a `MEDIA` header whose URL fixes no kind
 * (`file`) — sending a guess there is worse than sending nothing (BUG-589).
 */
export function templateHeaderSendMediaType(
  format?: string | null,
  url?: string | null,
): 'image' | 'video' | 'document' | undefined {
  const { requiresMedia, kind } = classifyHeaderFormat(format);
  if (!requiresMedia) return undefined;

  const resolved = kind ?? mediaKindFromUrl(url ?? '');
  return resolved === 'file' ? undefined : resolved;
}

/**
 * `accept` attribute for the header media file input. Deliberately wider than
 * what the pipeline supports (`image/*` instead of jpeg/png) so files like
 * iPhone HEIC photos stay pickable and get a clear post-pick error telling
 * the user to convert them — a greyed-out dialog reads as "no me deja
 * seleccionar ningún archivo".
 */
export function acceptForMediaKind(kind: tTemplateMediaKind): string {
  switch (kind) {
    case 'image':
      return 'image/*';
    case 'video':
      return 'video/*';
    case 'document':
      return 'application/pdf';
    default:
      return 'image/*, video/*, application/pdf';
  }
}

/**
 * Validates a picked file against the extensions the template's media kind
 * supports. Case-insensitive on the filename.
 */
export function isAllowedTemplateMediaFile(
  fileName: string,
  kind: tTemplateMediaKind,
): boolean {
  const lower = fileName.toLowerCase();
  return TEMPLATE_MEDIA_EXTENSIONS[kind].some((ext) => lower.endsWith(ext));
}

/**
 * Domains whose media we host ourselves. Deliberately narrower than
 * `TRUSTED_CDN_DOMAINS` in `src/functions/url.functions.ts`: that list also
 * carries Meta's CDNs, which is right for "may we skip the upload preflight?"
 * and wrong for "may we send this as the header without the user choosing it?".
 * Suffix matching covers `staging.leadsales.services` from the same entry.
 *
 * Counterpart in whatsapp-business-service: `isMetaCdnURL` /
 * `metaCdnHostSuffixes` (`internal/messaging/application/services/TemplateService.go`),
 * which gates the backend's scheduled-send media fallback. The two are
 * deliberately asymmetric — this one is a positive allowlist because the value
 * it judges is closer to user input, while the backend reads only stored
 * template components written by our own sync. Change one, read the other:
 * they enforce the same invariant ("never hand Meta a URL Meta hosts") and
 * neither is a substitute for the other. (BUG-818)
 */
const LEADSALES_MEDIA_DOMAINS = ['leadsales.services'];

/**
 * Minimal structural shape of a parsed template component. Declared locally so
 * this module stays free of `src/pages` and `src/components` imports; the
 * canonical `tComponent` is a superset of it.
 */
type tHeaderMediaComponent = {
  type?: string;
  text?: string;
  format?: string;
  example?: {
    url_example?: string[];
    header_handle?: string[];
  };
};

/**
 * True when `rawUrl` is safe to prefill as outbound header media on the user's
 * behalf: hosted by us, and acceptable to the send modal's own format gate.
 * Exported so every source of a prefill candidate — the components payload and
 * the flat url_example column — crosses one trust boundary instead of the
 * column being trusted implicitly (BUG-817).
 */
export function isSafeTemplateHeaderMediaUrl(rawUrl: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return false;
  }

  if (parsed.protocol !== 'https:') return false;
  if (!hostnameMatchesDomain(parsed.hostname, LEADSALES_MEDIA_DOMAINS)) {
    return false;
  }

  // Gate on the path, and feed the SAME string to both the kind lookup and
  // the extension check. Splitting on `?` left `#` attached, so
  // `.../evil.php#x.jpg` passed as an image; and deriving the kind from the
  // raw URL while checking the stripped one made `.../report.pdf?v=.png`
  // resolve as an image and fail — a legitimate hosted PDF that silently
  // refused to prefill. `pathname` drops query and fragment together.
  const { pathname } = parsed;
  return isAllowedTemplateMediaFile(pathname, mediaKindFromUrl(pathname));
}

/**
 * Resolves the header media URL a send modal may prefill on the user's behalf,
 * or `undefined` when there is nothing safe to prefill (BUG-817).
 *
 * Format-blind by design: IMAGE, VIDEO, DOCUMENT and the legacy generic MEDIA
 * headers all resolve through the same URL-shape inference the consumers
 * already use, so no `format` branch is needed here.
 *
 * Two gates, both deliberate:
 *
 * 1. **Host allowlist, not a Meta denylist.** The sibling auto-fill sites test
 *    `startsWith('https://scontent')`, which admits Meta's other hosts
 *    (`lookaside.fbsbx.com`, `mmg.whatsapp.net`) and every third-party host.
 *    Those URLs are signed and expire, so auto-attaching one sends a link Meta
 *    later fails to fetch.
 * 2. **The consumer's own send gate.** A URL the modal would reject on format
 *    must not prefill: the backend re-hosts a WebP source as `.webp`, which
 *    would swap today's actionable "provide your own media" guidance for a red
 *    invalid-format error on a URL the seller never typed.
 *
 * A text header resolves to nothing — the display chain starts at `text`, but
 * media prefill must not inherit that first link.
 */
export function resolveTemplateHeaderMediaUrl(
  components: tHeaderMediaComponent[] | null | undefined,
): string | undefined {
  if (!components?.length) return undefined;

  const header = components.find(
    (component) => component.type?.toUpperCase() === 'HEADER',
  );
  if (!header?.example) return undefined;
  // A text header never prefills media. Today it carries no example so this
  // is belt-and-braces, but the docblock states it as a guarantee, so enforce
  // it rather than relying on the payload shape.
  if (header.text) return undefined;

  const candidates = [
    header.example.url_example?.[0],
    header.example.header_handle?.[0],
  ].filter((value): value is string => !!value);

  return candidates.find(isSafeTemplateHeaderMediaUrl);
}
